import React from 'react'
import { Card, CardContent, Divider, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import LinkButton from '../Link/LinkButton'
import checkmarkSVG from '@talentinc/gatsby-theme-ecom/images/checkmark.svg'

export interface ReviewCardProps {
  packageName: string
  packageDescription: string
  price: number
  sellingPoints: string[]
  variant?: string | null
  packageCTA: string
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  packageName,
  packageDescription,
  price,
  sellingPoints,
  variant,
  packageCTA,
}) => {
  const { classes, cx } = useStyles()

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent
        className={cx({
          [classes.content]: true,
          [classes.contentInPost]: variant === 'inPost',
        })}
      >
        <Typography variant="h5">{packageName}</Typography>
        {variant !== 'inPost' && (
          <Typography className={classes.description} variant="body1">
            {packageDescription}
          </Typography>
        )}
        <Typography variant={variant === 'inPost' ? 'h3' : 'h5'} component="h6">
          ${price}
        </Typography>
        <Divider className={classes.divider} />
        <ul
          className={cx({
            [classes.sellingPoints]: true,
            [classes.sellingPointsInPost]: variant === 'inPost',
          })}
        >
          {sellingPoints.map((point) => (
            <Typography
              className={classes.sellingPoint}
              component="li"
              key={point}
              variant="body2"
            >
              {point}
            </Typography>
          ))}
        </ul>
        <LinkButton
          to="/resume-review/"
          color="secondary"
          variant="contained"
          fullWidth
          mixpanelTracking={null}
        >
          {packageCTA}
        </LinkButton>
      </CardContent>
    </Card>
  )
}

const useStyles = makeStyles()((theme) => ({
  card: {
    backgroundColor: theme.palette.background.default,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '32em',
    padding: '2.28em 2em 1.86em',

    '&:last-child': {
      paddingBottom: '2em',
    },
    [theme.breakpoints.down('md')]: {
      minHeight: 'initial',
    },
  },
  contentInPost: {
    minHeight: '27.5em',
    padding: '1.75em',

    '&:last-child': {
      paddingBottom: '1.75em',
    },

    [theme.breakpoints.down('lg')]: {
      minHeight: 'unset',
    },
  },
  description: {
    margin: '.39em 0 1.48em',
  },
  divider: {
    margin: '3em 0 1.06em',
    [theme.breakpoints.down('md')]: {
      margin: '1.36em 0 1.06em',
    },
  },
  sellingPoint: {
    padding: '.38em 0 .38em .25em',
  },
  sellingPoints: {
    listStyleImage: `url(${checkmarkSVG})`,
    margin: '0 0 2.4em',
    paddingLeft: '1.5em',
  },
  sellingPointsInPost: {
    margin: '0 0 1.25em',
  },
}))

export default ReviewCard
